import * as THREE from 'three'
import type {
  ImportedBrepRef,
  ImportedBodyEntry,
  ImportedPartBodyBrepBinding,
  ImportedPartBodyTopologyAsset,
  ImportedPartMeshBrepBinding,
  ImportedTopologySource,
  PartMeshMeta,
  PartPrimitiveMeta,
} from '../../imported-model/loaders/types'
import type {
  PuppyBrepIndexDocument,
  PuppyBrepIndexEntry,
  PuppyDisplayNodeBinding,
  PuppyDisplayNodeBodyBinding,
  PuppyDisplayPrimitiveFaceRange,
  PuppyParsedTopologyRuntimeAsset,
} from './types'
import { createPuppyBodyTopologyAssetKey } from './bodyTopologyAssetKey'
import { PuppyBodyTopologyRestorer } from './topology'

/**
 * `.puppy` primitive 在导出源 mesh 与导入可见 mesh 之间的三角面范围绑定。
 */
interface PuppyVisiblePrimitiveFaceRangeBinding {
  /** 离线包内稳定 primitive ID。 */
  primitiveId: string
  /** 导出阶段记录的源 mesh faceIndex 闭区间。 */
  sourceRange: PuppyDisplayPrimitiveFaceRange
  /** 导入后 display.glb 可见 mesh 的本地 faceIndex 闭区间。 */
  visibleRange: PuppyDisplayPrimitiveFaceRange
}

/**
 * `.puppy` display node 在导入后的可见 mesh 绑定。
 */
interface PuppyVisibleMeshBinding {
  /** 运行时 mesh ID。 */
  meshId: string
  /** 可见 mesh。 */
  mesh: THREE.Mesh
  /** 当前可见 mesh 对应的 body 绑定。 */
  bodyBindings: PuppyDisplayNodeBodyBinding[]
  /** 可见 mesh 本地 primitive faceIndex 范围。 */
  primitiveFaceRanges: PuppyDisplayPrimitiveFaceRange[]
  /** primitive 从导出源范围到导入可见范围的映射。 */
  primitiveFaceRangeBindings: PuppyVisiblePrimitiveFaceRangeBinding[]
  /** 原始 primitive ID 到当前 mesh 局部 primitive 下标的映射。 */
  localPrimitiveIndexById: Record<string, number>
}

/**
 * 读取 Object3D 上的 `.puppy` display node ID。
 *
 * @param object 场景对象。
 * @returns display node ID；不存在时返回 `null`。
 */
function readPuppyDisplayNodeId(object: THREE.Object3D): string | null {
  const displayNodeId = object.userData?.puppy?.displayNodeId
  return typeof displayNodeId === 'string' && displayNodeId.trim().length > 0
    ? displayNodeId.trim()
    : null
}

/**
 * 建立 display node ID 到导入后 Object3D 的索引。
 *
 * @param root display.glb 根节点。
 * @returns display node 索引。
 */
function collectDisplayNodeObjects(root: THREE.Object3D): Map<string, THREE.Object3D[]> {
  const objectsByDisplayNodeId = new Map<string, THREE.Object3D[]>()
  root.traverse((object) => {
    const displayNodeId = readPuppyDisplayNodeId(object)
    if (!displayNodeId) {
      return
    }

    // GLTFLoader 的材质子 Mesh 可以继承节点 extras；同一个 display host 只能登记一次。
    let ancestor = object.parent
    while (ancestor) {
      if (readPuppyDisplayNodeId(ancestor) === displayNodeId) return
      ancestor = ancestor.parent
    }

    const bucket = objectsByDisplayNodeId.get(displayNodeId) ?? []
    bucket.push(object)
    objectsByDisplayNodeId.set(displayNodeId, bucket)
  })
  return objectsByDisplayNodeId
}

/**
 * 计算 mesh 的完整 faceIndex 范围。
 *
 * @param mesh 目标 mesh。
 * @returns faceIndex 范围。
 */
function createFullMeshPrimitiveFaceRange(mesh: THREE.Mesh, primitiveId: string): PuppyDisplayPrimitiveFaceRange {
  const indexCount = mesh.geometry.index?.count
  const positionCount = mesh.geometry.getAttribute('position')?.count ?? 0
  const triangleCount = Math.max(0, Math.floor((indexCount ?? positionCount) / 3))
  return {
    primitiveId,
    startFaceIndex: 0,
    endFaceIndex: Math.max(0, triangleCount - 1),
  }
}

/**
 * 从 display node host 收集实际参与 raycast 的可见 mesh。
 *
 * @param host 携带 displayNodeId 的导入对象。
 * @returns 可见 mesh 列表。
 */
function collectHostMeshes(host: THREE.Object3D): THREE.Mesh[] {
  if (host instanceof THREE.Mesh) {
    return [host]
  }

  const meshes: THREE.Mesh[] = []
  host.children.forEach((child) => {
    if (child instanceof THREE.Mesh) {
      meshes.push(child)
    }
  })
  return meshes
}

/**
 * 根据 primitive ID 读取 body 绑定中的原始下标。
 *
 * @param binding body 绑定。
 * @param primitiveId primitive ID。
 * @returns 原始 primitive 下标；不存在时返回 `null`。
 */
function resolvePrimitiveIndex(binding: PuppyDisplayNodeBodyBinding, primitiveId: string): number | null {
  const index = binding.primitiveIds.indexOf(primitiveId)
  return index >= 0 ? binding.primitiveIndices[index] ?? null : null
}

/**
 * 查找 body 绑定内某个 primitive 的 faceIndex 范围。
 *
 * @param binding body 绑定。
 * @param primitiveId primitive ID。
 * @returns primitive faceIndex 范围。
 */
function findPrimitiveFaceRange(
  binding: PuppyDisplayNodeBodyBinding,
  primitiveId: string,
): PuppyDisplayPrimitiveFaceRange {
  const range = binding.primitiveFaceRanges.find((item) => item.primitiveId === primitiveId)
  if (!range) {
    throw new Error(`PuppyCAD 离线包缺少 primitive face 范围: body=${binding.bodyBindingId}, primitive=${primitiveId}`)
  }
  return range
}

/**
 * 构建 primitive 范围映射并按 primitive ID 去重。
 *
 * @param bindings primitive 范围映射候选。
 * @returns 去重后的 primitive 范围映射。
 */
function dedupePrimitiveFaceRangeBindings(
  bindings: PuppyVisiblePrimitiveFaceRangeBinding[],
): PuppyVisiblePrimitiveFaceRangeBinding[] {
  const bindingByPrimitiveId = new Map<string, PuppyVisiblePrimitiveFaceRangeBinding>()
  bindings.forEach((binding) => {
    if (!bindingByPrimitiveId.has(binding.primitiveId)) {
      bindingByPrimitiveId.set(binding.primitiveId, binding)
    }
  })
  return Array.from(bindingByPrimitiveId.values())
}

/**
 * 将单个 display node 映射到可见 mesh 运行时绑定。
 *
 * @param input 创建输入。
 * @returns 可见 mesh 绑定列表。
 */
function createVisibleMeshBindings(input: {
  nodeBinding: PuppyDisplayNodeBinding
  displayNodeHost: THREE.Object3D
  hostIndex: number
}): PuppyVisibleMeshBinding[] {
  const meshes = collectHostMeshes(input.displayNodeHost)
  if (meshes.length === 0) {
    return []
  }

  if (meshes.length === 1) {
    const meshId = input.hostIndex === 0
      ? input.nodeBinding.displayNodeId
      : `${input.nodeBinding.displayNodeId}:instance:${input.hostIndex}`
    const primitiveIds = Array.from(new Set(input.nodeBinding.bodyBindings.flatMap((binding) => binding.primitiveIds)))
    const localPrimitiveIndexById = Object.fromEntries(
      primitiveIds.map((primitiveId) => [
        primitiveId,
        input.nodeBinding.bodyBindings
          .map((binding) => resolvePrimitiveIndex(binding, primitiveId))
          .find((index): index is number => typeof index === 'number') ?? 0,
      ]),
    )
    return [{
      meshId,
      mesh: meshes[0]!,
      bodyBindings: input.nodeBinding.bodyBindings,
      primitiveFaceRanges: input.nodeBinding.bodyBindings.flatMap((binding) => binding.primitiveFaceRanges),
      primitiveFaceRangeBindings: dedupePrimitiveFaceRangeBindings(
        input.nodeBinding.bodyBindings.flatMap((binding) => (
          binding.primitiveIds.map((primitiveId) => {
            const range = findPrimitiveFaceRange(binding, primitiveId)
            return {
              primitiveId,
              sourceRange: range,
              visibleRange: range,
            }
          })
        )),
      ),
      localPrimitiveIndexById,
    }]
  }

  return meshes.map((mesh, meshIndex) => {
    const meshId = `${input.nodeBinding.displayNodeId}:primitive-mesh:${input.hostIndex}:${meshIndex}`
    const sourceBindingByPrimitiveId = new Map<string, PuppyDisplayNodeBodyBinding>()
    const bodyBindings = input.nodeBinding.bodyBindings
      .map((binding): PuppyDisplayNodeBodyBinding | null => {
        const bindingPrimitiveIds = binding.primitiveIds.filter((_primitiveId, index) => (
          binding.primitiveIndices[index] === meshIndex
        ))
        if (bindingPrimitiveIds.length === 0) {
          return null
        }

        bindingPrimitiveIds.forEach((primitiveId) => {
          sourceBindingByPrimitiveId.set(primitiveId, binding)
        })
        return {
          ...binding,
          primitiveIds: bindingPrimitiveIds,
          primitiveIndices: bindingPrimitiveIds.map(() => 0),
          primitiveFaceRanges: bindingPrimitiveIds.map((primitiveId) => (
            createFullMeshPrimitiveFaceRange(mesh, primitiveId)
          )),
        }
      })
      .filter((binding): binding is PuppyDisplayNodeBodyBinding => binding !== null)

    return {
      meshId,
      mesh,
      bodyBindings,
      primitiveFaceRanges: bodyBindings.flatMap((binding) => binding.primitiveFaceRanges),
      primitiveFaceRangeBindings: dedupePrimitiveFaceRangeBindings(
        bodyBindings.flatMap((binding) => (
          binding.primitiveIds.map((primitiveId) => {
            const sourceBinding = sourceBindingByPrimitiveId.get(primitiveId)
            if (!sourceBinding) {
              throw new Error(`PuppyCAD 离线包缺少 primitive 源绑定: ${primitiveId}`)
            }
            return {
              primitiveId,
              sourceRange: findPrimitiveFaceRange(sourceBinding, primitiveId),
              visibleRange: findPrimitiveFaceRange(binding, primitiveId),
            }
          })
        )),
      ),
      localPrimitiveIndexById: Object.fromEntries(
        bodyBindings.flatMap((binding) => binding.primitiveIds).map((primitiveId) => [primitiveId, 0]),
      ),
    }
  }).filter((binding) => binding.bodyBindings.length > 0)
}

/**
 * 读取材质名称。
 *
 * @param material mesh 材质。
 * @returns 材质名称列表。
 */
function collectMaterialNames(material: THREE.Material | THREE.Material[]): string[] {
  const materials = Array.isArray(material) ? material : [material]
  return materials
    .map((item) => item.name)
    .filter((name): name is string => typeof name === 'string' && name.trim().length > 0)
}


/**
 * 创建导入 BRep 引用。
 *
 * @param entry BRep 索引条目。
 * @returns BRep 引用。
 */
function createBrepRef(entry: PuppyBrepIndexEntry): ImportedBrepRef {
  return {
    uri: entry.brepPath,
    key: entry.geometryObjectId,
    sha256: entry.brepSha256,
    byteLength: entry.byteLength,
    encoding: entry.encoding,
    storage: 'puppy-package',
  }
}

/**
 * 将 body 运行时绑定写入可见 mesh 的 userData。
 *
 * @param input 写入输入。
 */
/**
 * 解析 body 的用户可读显示名。
 *
 * @param binding display body 绑定。
 * @param index 当前 body 在可见 mesh 内的序号。
 * @returns 设计树可直接显示的实体名。
 */
function resolveBodyDisplayLabel(binding: PuppyDisplayNodeBodyBinding | undefined, index: number): string {
  const readableName = binding?.name?.trim()
  return readableName && !readableName.startsWith('mesh:') && !readableName.includes('::')
    ? readableName
    : `实体(${index + 1})`
}

function applyVisibleMeshUserData(input: {
  meshBinding: PuppyVisibleMeshBinding
  runtimeBodyBindings: ImportedPartBodyBrepBinding[]
  bodyTopologyAssets: Readonly<Record<string, ImportedPartBodyTopologyAsset>>
}): void {
  const bodyEntries: ImportedBodyEntry[] = input.runtimeBodyBindings.map((binding, index) => ({
    bodyId: binding.bodyId ?? binding.bodyBindingId,
    bodyBindingId: binding.bodyBindingId,
    geometryObjectId: input.bodyTopologyAssets[binding.bodyBindingId]?.geometryObjectId,
    label: input.meshBinding.bodyBindings[index]?.name
      ?? input.meshBinding.bodyBindings[index]?.bodyId
      ?? `实体${index + 1}`,
  }))
  bodyEntries.forEach((entry, index) => {
    if (entry.label.startsWith('mesh:') || entry.label.includes('::') || entry.label === entry.bodyId) {
      entry.label = `实体(${index + 1})`
    }
  })
  const primitiveEntityEntries = input.runtimeBodyBindings.flatMap((binding, index) => (
    binding.primitiveIds.map((primitiveId) => ({
      primitiveIndex: input.meshBinding.localPrimitiveIndexById[primitiveId] ?? 0,
      bodyId: binding.bodyId ?? binding.bodyBindingId,
      bodyBindingId: binding.bodyBindingId,
      label: bodyEntries[index]?.label ?? binding.bodyId ?? binding.bodyBindingId,
    }))
  ))

  input.meshBinding.mesh.userData = {
    ...input.meshBinding.mesh.userData,
    partAssetLocal: {
      meshId: input.meshBinding.meshId,
      primitiveIds: Array.from(new Set(input.runtimeBodyBindings.flatMap((binding) => binding.primitiveIds))),
      primitiveFaceRanges: input.meshBinding.primitiveFaceRanges,
    },
    faceTriangleRanges: input.meshBinding.primitiveFaceRanges.map((range) => [
      range.startFaceIndex,
      range.endFaceIndex + 1,
    ]),
    importedPartBodyEntries: bodyEntries,
    primitiveEntityEntries,
  }
}

/**
 * 生成运行时 body 绑定 ID。
 *
 * @param input 绑定输入。
 * @returns 运行时 body 绑定 ID。
 */
function createRuntimeBodyBindingId(input: {
  bodyBindingId: string
  geometryObjectId: string
  meshId: string
  sharedMeshCount: number
  sharedBodyBindingCount: number
}): string {
  return input.sharedMeshCount <= 1 && input.sharedBodyBindingCount <= 1
    ? input.bodyBindingId
    : `${input.bodyBindingId}::geometry:${input.geometryObjectId}::${input.meshId}`
}

/**
 * 构建 `.puppy` 导入模型的拓扑源，并标注可见 GLB mesh。
 *
 * @param input 构建输入。
 * @returns imported-model runtime 可消费的拓扑源。
 */
export function buildPuppyImportedTopologySource(input: {
  /** display.glb 根节点。 */
  root: THREE.Object3D
  /** `.puppy` BRep 索引。 */
  brepIndex: PuppyBrepIndexDocument
  /** `.puppy` 自带的 body 级拓扑 runtime 资产。 */
  topologyAssetsByKey: Map<string, PuppyParsedTopologyRuntimeAsset>
  /** 按 `.puppy` 包内来源 geometryObjectId 索引的 Worker Shape-only 注册结果。 */
  registeredShapeBySourceGeometryObjectId: Map<string, RegisterImportedBrepShapeResult>
}): ImportedTopologySource {
  const displayNodeObjects = collectDisplayNodeObjects(input.root)
  const entriesByGeometryObjectId = new Map(
    input.brepIndex.entries.map((entry) => [entry.geometryObjectId, entry]),
  )
  const bodyEntriesById = new Map<string, ImportedBodyEntry>()
  const bodyPrimitiveIndicesById: Record<string, number[]> = {}
  const primitiveCatalog: Record<string, PartPrimitiveMeta> = {}
  const meshCatalog: Record<string, PartMeshMeta> = {}
  const brepBindingsByMeshId: Record<string, ImportedPartMeshBrepBinding> = {}
  const bodyBrepBindings: Record<string, ImportedPartBodyBrepBinding> = {}
  const bodyBrepBindingIdByPrimitiveId: Record<string, string> = {}
  const bodyTopologyAssets: Record<string, ImportedPartBodyTopologyAsset> = {}
  const topologyRestorer = new PuppyBodyTopologyRestorer()
  let registeredBodyCount = 0
  const bodyBindingUseCountById = new Map<string, number>()
  input.brepIndex.displayNodes.forEach((displayNode) => {
    displayNode.bodyBindings.forEach((bodyBinding) => {
      bodyBindingUseCountById.set(
        bodyBinding.bodyBindingId,
        (bodyBindingUseCountById.get(bodyBinding.bodyBindingId) ?? 0) + 1,
      )
    })
  })

  input.brepIndex.displayNodes.forEach((nodeBinding) => {
    const hosts = displayNodeObjects.get(nodeBinding.displayNodeId) ?? []
    if (hosts.length === 0) {
      throw new Error(`PuppyCAD 离线包 display.glb 缺少节点绑定: ${nodeBinding.displayNodeId}`)
    }

    hosts.forEach((host, hostIndex) => {
      const meshBindings = createVisibleMeshBindings({
        nodeBinding,
        displayNodeHost: host,
        hostIndex,
      })

      meshBindings.forEach((meshBinding) => {
        const runtimeBodyBindings = meshBinding.bodyBindings.map((bodyBinding) => {
          const entry = entriesByGeometryObjectId.get(bodyBinding.geometryObjectId)
          const sourceTopologyAsset = input.topologyAssetsByKey.get(createPuppyBodyTopologyAssetKey(bodyBinding))
          const registeredShape = input.registeredShapeBySourceGeometryObjectId.get(
            bodyBinding.geometryObjectId,
          )
          if (!entry || !sourceTopologyAsset) {
            throw new Error(`PuppyCAD 离线包缺少 body 拓扑快照资产: ${bodyBinding.geometryObjectId}`)
          }

          const runtimeBodyBindingId = createRuntimeBodyBindingId({
            bodyBindingId: bodyBinding.bodyBindingId,
            geometryObjectId: bodyBinding.geometryObjectId,
            meshId: meshBinding.meshId,
            sharedMeshCount: meshBindings.length,
            sharedBodyBindingCount: bodyBindingUseCountById.get(bodyBinding.bodyBindingId) ?? 1,
          })
          const brepRef = createBrepRef(entry)
          const runtimeBinding: ImportedPartBodyBrepBinding = {
            bodyBindingId: runtimeBodyBindingId,
            meshId: meshBinding.meshId,
            bodyId: bodyBinding.bodyId,
            primitiveIds: bodyBinding.primitiveIds,
            primitiveIndices: bodyBinding.primitiveIds.map((primitiveId) => (
              meshBinding.localPrimitiveIndexById[primitiveId] ?? 0
            )),
            // 局部范围由完整 Body 收集完成后的统一重建阶段写入。
            topologyFaceTriangleRanges: [],
            brepRef,
            sourceLevel: 'body',
          }

          bodyEntriesById.set(bodyBinding.bodyId, {
            bodyId: bodyBinding.bodyId,
            bodyBindingId: runtimeBodyBindingId,
            geometryObjectId: registeredShape?.geometryObjectId ?? bodyBinding.geometryObjectId,
            label: resolveBodyDisplayLabel(bodyBinding, bodyEntriesById.size),
          })
          bodyPrimitiveIndicesById[bodyBinding.bodyId] = Array.from(new Set([
            ...(bodyPrimitiveIndicesById[bodyBinding.bodyId] ?? []),
            ...runtimeBinding.primitiveIndices,
          ]))
          bodyBrepBindings[runtimeBodyBindingId] = runtimeBinding
          bodyTopologyAssets[runtimeBodyBindingId] = {
            bodyBindingId: runtimeBodyBindingId,
            geometryObjectId: registeredShape?.geometryObjectId ?? sourceTopologyAsset.geometryObjectId,
            exportDefinitionId: sourceTopologyAsset.exportDefinitionId ?? bodyBinding.geometryObjectId,
            meshData: {
              id: `${meshBinding.meshId}:${runtimeBodyBindingId}`,
              positions: new Float32Array(0),
              normals: new Float32Array(0),
              metadata: {
                ...(sourceTopologyAsset.metadata ?? {}),
                objectId: registeredShape?.geometryObjectId ?? sourceTopologyAsset.geometryObjectId,
                bodyBindingId: runtimeBodyBindingId,
                bodyId: bodyBinding.bodyId,
                meshId: meshBinding.meshId,
                source: 'puppy-topology-runtime',
                sourceLevel: 'body',
              },
            },
          }
          topologyRestorer.add({
            // 同一业务 occurrence 的材质共享完整 Body，不同 occurrence 不混入彼此的显示几何。
            ownerKey: JSON.stringify([
              nodeBinding.assemblyNodeId ?? nodeBinding.displayNodeId,
              hostIndex,
              bodyBinding.geometryObjectId,
              bodyBinding.bodyId,
            ]),
            mesh: meshBinding.mesh,
            sourceAsset: sourceTopologyAsset,
            displayBinding: bodyBinding,
            primitiveProjections: meshBinding.primitiveFaceRangeBindings
              .filter(projection => bodyBinding.primitiveIds.includes(projection.primitiveId))
              .map(projection => ({
                sourceRange: [projection.sourceRange.startFaceIndex, projection.sourceRange.endFaceIndex + 1],
                visibleRange: [projection.visibleRange.startFaceIndex, projection.visibleRange.endFaceIndex + 1],
              })),
            runtimeBinding,
            runtimeAsset: bodyTopologyAssets[runtimeBodyBindingId]!,
          })
          runtimeBinding.primitiveIds.forEach((primitiveId) => {
            bodyBrepBindingIdByPrimitiveId[primitiveId] = runtimeBodyBindingId
            primitiveCatalog[primitiveId] = {
              primitiveId,
              meshId: meshBinding.meshId,
              primitiveIndex: meshBinding.localPrimitiveIndexById[primitiveId] ?? 0,
              semanticKind: 'primitive',
              brepRef,
            }
          })
          registeredBodyCount += 1
          return runtimeBinding
        })

        meshCatalog[meshBinding.meshId] = {
          meshId: meshBinding.meshId,
          objectName: meshBinding.mesh.name,
          primitiveIds: Array.from(new Set(runtimeBodyBindings.flatMap((binding) => binding.primitiveIds))),
          materialNames: collectMaterialNames(meshBinding.mesh.material),
        }
        const firstBodyBinding = runtimeBodyBindings[0]
        if (firstBodyBinding?.brepRef) {
          brepBindingsByMeshId[meshBinding.meshId] = {
            meshId: meshBinding.meshId,
            bodyId: firstBodyBinding.bodyId,
            primitiveId: firstBodyBinding.primitiveIds[0],
            brepRef: firstBodyBinding.brepRef,
          }
        }
        applyVisibleMeshUserData({
          meshBinding,
          runtimeBodyBindings,
          bodyTopologyAssets,
        })
      })
    })
  })

  if (registeredBodyCount <= 0) {
    throw new Error('PuppyCAD 离线包没有可恢复的 body 拓扑绑定')
  }

  // 完整 Body 和局部映射全部校验成功后，调用方才可将拓扑源交给 Repository。
  topologyRestorer.finalize()

  return {
    bodyEntries: Array.from(bodyEntriesById.values()),
    bodyPrimitiveIndicesById,
    primitiveCatalog,
    meshCatalog,
    brepBindingsByMeshId,
    bodyBrepBindings,
    bodyBrepBindingIdByPrimitiveId,
    bodyTopologyAssets,
  }
}
